/**
 * #10765 (H3, EPIC #9970): the homepage live strip under the hero.
 *
 * Three cells, each read from one public CMS route on www (cms-content-worker, published only):
 *   next event            /api/content/events?when=upcoming&limit=1
 *   latest post           /api/content/posts?type=blog&limit=1
 *   featured testimonial  /api/content/testimonials?featured=true&limit=1
 * A source with nothing (or a failed read) gives a calm empty cell that links to its index page.
 * No third-party widget: the www CSP stays as it is. Pure, so it is tested in node.
 */
import type { SiteEvent } from '../../lib/events';
import { on } from '../../lib/client-quote';
import type { Post, Testimonial } from './studio-kit/data';

/** Site-relative paths, read through at() in ./studio-kit/data. */
export const LIVE_STRIP_SOURCES = {
  event: 'api/content/events?when=upcoming&limit=1',
  post: 'api/content/posts?type=blog&limit=1',
  // The route reads featured=true, not featured=1 (cms-content-worker api/testimonials.ts).
  testimonial: 'api/content/testimonials?featured=true&limit=1',
} as const;

export interface LiveCell {
  kind: 'event' | 'post' | 'testimonial';
  label: string;
  /** The headline: an event or post title, or a client quote. */
  text: string;
  /** A short line under the headline (the event time, the post category, who said it). */
  meta: string | null;
  href: string;
  cta: string;
  empty: boolean;
}

const PT = 'America/Los_Angeles';

export function formatEventTime(e: Pick<SiteEvent, 'starts_at' | 'timezone'>): string {
  const tz = e.timezone || PT;
  const d = new Date(e.starts_at);
  const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }).format(d);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }).format(d);
  return `${day} · ${time.replace(':00', '')}`;
}

/** The first row of a CMS list body (`{ success, data: [...] }`), or null. */
export function firstOf<T>(body: unknown): T | null {
  const rows = (body as { data?: unknown } | null)?.data;
  return Array.isArray(rows) && rows.length > 0 ? (rows[0] as T) : null;
}

export function eventCell(e: SiteEvent | null): LiveCell {
  if (!e) {
    return {
      kind: 'event',
      label: 'Live sessions',
      text: 'No live session on the calendar right now.',
      meta: null,
      href: '/events',
      cta: 'See past sessions',
      empty: true,
    };
  }
  return {
    kind: 'event',
    label: 'Next live session',
    text: e.title,
    meta: formatEventTime(e),
    href: e.eventbrite_url || `/events/${e.slug}`,
    cta: 'Save a seat',
    empty: false,
  };
}

export function postCell(p: Post | null): LiveCell {
  if (!p) {
    return {
      kind: 'post',
      label: 'Field notes',
      text: 'New field notes are on the way.',
      meta: null,
      href: '/blog',
      cta: 'Browse the blog',
      empty: true,
    };
  }
  return {
    kind: 'post',
    label: 'Latest field note',
    text: p.title,
    meta: p.category || null,
    href: `/blog/${p.slug}`,
    cta: 'Read it',
    empty: false,
  };
}

/**
 * Who said it. A name or company shows only when its consent flag is on; a NULL, false or "0"
 * flag hides it (fail closed, client-quote.ts on()).
 */
export function attribution(t: Testimonial): string {
  const name = on(t.show_client_name) && t.client_name ? t.client_name : 'Client';
  return on(t.show_company_name) && t.client_company ? `${name} · ${t.client_company}` : name;
}

export function testimonialCell(t: Testimonial | null): LiveCell {
  if (!t) {
    return {
      kind: 'testimonial',
      label: 'In their words',
      text: 'Hear from the owners we work with.',
      meta: null,
      href: '/testimonials',
      cta: 'Read their stories',
      empty: true,
    };
  }
  return {
    kind: 'testimonial',
    label: 'In their words',
    text: t.quote,
    meta: attribution(t),
    href: '/testimonials',
    cta: 'More stories',
    empty: false,
  };
}
